import { HttpClient, HttpEventType, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { map, take, switchMap, tap, filter, catchError } from 'rxjs/operators';
import { RestrictionsQueries } from '../graphql/queries/restrictions.queries';
import { ApolloClientService } from './apollo-client.service';
import { DrivingRestrictionFileInfo, DrivingRestrictionUploadFileResponse } from '../models/file-type-driving-restriction-enum';
import { UploadedFile } from '../models/fileUpload';


@Injectable({
  providedIn: 'root',
})
export class FileService {
  constructor(
    private apolloClientService: ApolloClientService,
    private http: HttpClient
  ) {}

  /* ================================
     GraphQL File Handler Methods
  ================================ */

  /** Retrieves file info (FileName, FileUid) for a stored file. */
  retrieveFileInfo(FileUid: string): Observable<DrivingRestrictionFileInfo> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.query<{ fileHandlerDrivingRestrictionRetrieveFileInfo: DrivingRestrictionFileInfo }>({
          query: RestrictionsQueries.retrieveFileInfo,
          variables: { FileUid },
          fetchPolicy: 'no-cache'
        })
      ),
      take(1),
      map(res => res.data.fileHandlerDrivingRestrictionRetrieveFileInfo)
    );
  }

  /** Gets a pre-signed download URL for an existing file. */
  generatePresignedDownloadUrl(FileUid: string): Observable<string> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.query<{ fileHandlerDrivingRestrictionGeneratePresignedDownloadUrl: string }>({
          query: RestrictionsQueries.generatePresignedDownloadUrl,
          variables: { FileUid },
          fetchPolicy: 'no-cache'
        })
      ),
      take(1),
      map(res => res.data.fileHandlerDrivingRestrictionGeneratePresignedDownloadUrl)
    );
  }

  /** Requests upload info from backend (FileUid + PreSignUrl). */
  requestUploadFile(FileName: string, Source: string): Observable<DrivingRestrictionUploadFileResponse> {
    return this.apolloClientService.abstractionLayerClient$.pipe(
      switchMap(client =>
        client.mutate<{ fileHandlerDrivingRestrictionUploadFile: DrivingRestrictionUploadFileResponse }>({
          mutation: RestrictionsQueries.uploadFile,
          variables: { FileName, Source },
          context: { headers: { 'ShowLoading': 'true' } }
        })
      ),
      map(res => res.data!.fileHandlerDrivingRestrictionUploadFile),
      tap(resp => console.debug('[requestUploadFile] server resp:', resp)) // <—
    );
  }


  /* ================================
     Raw HTTP with pre-signed URLs
  ================================ */

  /**
   * Uploads a file to S3 using a pre-signed URL.
   */
  uploadFile(file: UploadedFile, url: string) {
    if (!file?.file) throw new Error(`Invalid file passed for upload: ${file?.name}`);

    const headers = new HttpHeaders(
      file.file.type ? { 'Content-Type': file.file.type } : {}
    );

    return this.http.put(url, file.file, {
      headers,
      observe: 'events',
      reportProgress: true,
      withCredentials: false
    }).pipe(
      filter(ev => ev.type === HttpEventType.Response),
      map(() => true),
      take(1),
      catchError(err => {
        console.error('S3 upload failed:', { url, err });
        return throwError(() => err);
      })
    );
  }


  /**
   * Downloads a file from S3 using a pre-signed URL.
   */
  downloadFile(presignedUrl: string, fileName: string): void {
    this.http.get(presignedUrl, { responseType: 'blob' }).pipe(take(1)).subscribe({
      next: (blob) => {
        const url = window.URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.style.display = 'none';
        document.body.appendChild(anchor);
        anchor.click();
        setTimeout(() => {
          window.URL.revokeObjectURL(url);
          document.body.removeChild(anchor);
        }, 200);
      },
      error: (err) => {
        console.error('Failed to download file:', err);
      }
    });
  }
}
